import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { isReadOnlyUser } from '../projects/types';
import { useChatPeople, useCreateDm } from './hooks';
import { roomErrorMessage } from './rooms';

/**
 * "Message": opens (or starts) the direct message with one person and goes to
 * it on the Messages page. Calls create_dm (20270180000100), which returns the
 * existing DM from either side, so pressing it twice never makes two.
 *
 * Rendered only when the database would accept it:
 * - the signed-in person is an approved TAP admin or member (create_dm refuses
 *   a viewer, a supplier and anyone inactive), and
 * - the other person is an active TAP admin or member (useChatPeople is that
 *   exact list), and is not the signed-in person.
 * Anything else renders nothing rather than a button that would be refused.
 */
export function MessagePersonButton({ userId, name }: { userId: string; name: string }) {
  const { access } = useAuth();
  const navigate = useNavigate();
  const mayStart =
    access.status === 'approved' &&
    access.profile.account_type === 'tap' &&
    !isReadOnlyUser(access.profile) &&
    access.profile.id !== userId;
  const people = useChatPeople(mayStart);
  const createDm = useCreateDm();
  const [error, setError] = useState('');

  if (!mayStart || !(people.data ?? []).some((person) => person.id === userId)) {
    return null;
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        aria-label={`Message ${name}`}
        disabled={createDm.isPending}
        onClick={() => {
          setError('');
          createDm.mutate(userId, {
            onSuccess: (roomId) => {
              void navigate(`/messages?room=${encodeURIComponent(roomId)}`);
            },
            onError: (cause) =>
              setError(roomErrorMessage(cause instanceof Error ? cause.message : '')),
          });
        }}
      >
        Message
      </Button>
      {error === '' ? null : (
        <span role="alert" className="message-person-error">
          {error}
        </span>
      )}
    </>
  );
}
